import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Download, Sparkles, RefreshCw, WifiOff } from "lucide-react";
import { skipWaitingAndReload } from "@/lib/registerSW";

const DISMISS_KEY = "taru_pwa_dismissed_at";
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator.standalone === true;
}

/**
 * PWAExperience — one component handles:
 *  1. Install prompt (bottom-right card on desktop, bottom sheet on mobile)
 *  2. Offline indicator (top bar when navigator.onLine is false)
 *  3. Update-ready toast (when a new service worker is waiting)
 */
export function PWAExperience() {
  const [deferred, setDeferred] = useState(null);
  const [installVisible, setInstallVisible] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);

  // --- Install prompt capture ---
  useEffect(() => {
    if (isStandalone()) return; // already installed
    const last = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (last && Date.now() - last < COOLDOWN_MS) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Small delay so it doesn't collide with page load
      setTimeout(() => setInstallVisible(true), 3500);
    };
    const onInstalled = () => {
      setInstallVisible(false);
      setDeferred(null);
      toast.success("TARU installed — you can now launch from your home screen.");
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // --- Online / offline listener ---
  useEffect(() => {
    const on = () => { setOffline(false); toast.success("Back online"); };
    const off = () => { setOffline(true); };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // --- Service-worker update ready ---
  useEffect(() => {
    const handler = () => {
      toast(
        (t) => (
          <div className="flex items-center gap-3">
            <RefreshCw size={16} className="text-[#0F5E39]"/>
            <span className="text-sm">A new version of TARU is ready.</span>
            <button
              onClick={() => { toast.dismiss(t); skipWaitingAndReload(); }}
              className="text-sm font-medium text-[#0F5E39] underline"
              data-testid="pwa-reload-btn"
            >
              Refresh
            </button>
          </div>
        ),
        { duration: Infinity, id: "sw-update" }
      );
    };
    window.addEventListener("taru:sw-update-ready", handler);
    return () => window.removeEventListener("taru:sw-update-ready", handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") {
        // 'appinstalled' will handle the toast
      } else {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      }
    } finally {
      setDeferred(null);
      setInstallVisible(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setInstallVisible(false);
  };

  return (
    <>
      {offline && (
        <div className="fixed top-0 inset-x-0 z-[60] bg-[#7A0D46] text-white text-center py-1.5 text-xs font-medium shadow-md" data-testid="offline-indicator">
          <span className="inline-flex items-center gap-2">
            <WifiOff size={12}/> You're offline · showing cached data
          </span>
        </div>
      )}

      {installVisible && deferred && (
        <div
          className="fixed z-40 max-w-sm bg-white border border-[#E4DFCF] rounded-2xl shadow-2xl overflow-hidden
                     bottom-4 left-4 right-4 md:right-6 md:left-auto md:bottom-6"
          data-testid="pwa-install-card"
        >
          <div className="p-4 pr-3">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#083F22] flex items-center justify-center shrink-0">
                <Sparkles size={20} className="text-[#C9A24B]"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg leading-tight">Install TARU</div>
                <div className="text-xs text-[#4A5A50] mt-1">
                  Add to your home screen for one-tap access, faster load, and offline reading.
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={install}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white text-xs font-medium"
                    data-testid="pwa-install-btn"
                  >
                    <Download size={12}/> Install
                  </button>
                  <button
                    onClick={dismiss}
                    className="text-xs text-[#4A5A50] hover:text-[#0F1E15] px-2 py-1"
                    data-testid="pwa-install-later"
                  >
                    Maybe later
                  </button>
                </div>
              </div>
              <button
                onClick={dismiss}
                className="text-[#4A5A50] hover:text-[#0F1E15] p-1"
                aria-label="Dismiss"
                data-testid="pwa-install-close"
              >
                <X size={16}/>
              </button>
            </div>
          </div>
          <div className="h-1 bg-gradient-to-r from-[#0F5E39] via-[#C9A24B] to-[#0F5E39]"/>
        </div>
      )}
    </>
  );
}
